import React, { useState } from 'react';
import CustomDropdown from './CustomDropdown';
import styles from './DurationScrubber.module.css';

// Log time the fast way: drag the duration scrubber or tap a preset.
// Start time is the anchor and only changes when edited directly. Duration
// and end time are two views of the same span: editing either one recomputes
// the other against the fixed start (end = start + duration).
//
// onChange receives { hours, startTime, endTime } so each consumer can store
// whatever its model needs:
//   - Timesheets keep start_time / end_time (duration is derived from them).
//   - Schedule keeps hours_per_day + start_time/end_time + is_time_specific
//     (pass `hours` as the explicit source of truth and control `startOn`).

const MAX_HOURS = 12;
const DEFAULT_START = 540; // 09:00 in minutes-since-midnight

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

const fmtClock = (mins) => {
  const norm = (((Math.round(mins) % 1440) + 1440) % 1440);
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  const ap = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${pad(m)} ${ap}`;
};

const fmtDur = (hours) => {
  if (hours <= 0.0001) return '0h';
  let H = Math.floor(hours + 1e-9);
  let M = Math.round((hours - H) * 60);
  if (M === 60) { H += 1; M = 0; }
  if (M === 0) return `${H}h`;
  if (H === 0) return `${M}m`;
  return `${H}h ${M}m`;
};

const parseHM = (value, fallback) => {
  if (!value || typeof value !== 'string') return fallback;
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h)) return fallback;
  return h * 60 + (m || 0);
};

const toHM = (mins) => {
  const clamped = Math.max(0, Math.min(24 * 60, Math.round(mins)));
  return `${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
};

// Parse a typed duration into decimal hours. Accepts "7h 30m", "7h", "30m",
// "7:30", "1.5" / "1,5" (decimal hours) and bare "7". Returns null when the
// input can't be understood so the caller can flag it as invalid.
const parseDuration = (input) => {
  if (input == null) return null;
  const s = String(input).trim().toLowerCase().replace(',', '.');
  if (!s) return null;

  // h:mm
  let m = s.match(/^(\d{1,2}):([0-5]?\d)$/);
  if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;

  // "7h 30m" / "7h" / "30m" (either part optional, but at least one present)
  m = s.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/);
  if (m && (m[1] || m[2])) {
    const h = m[1] ? parseFloat(m[1]) : 0;
    const mm = m[2] ? parseInt(m[2], 10) : 0;
    if (mm > 59) return null;
    return h + mm / 60;
  }

  // bare number = hours
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);

  return null;
};

export default function DurationScrubber({
  hours: hoursProp,
  startTime,
  endTime,
  startOn: startOnProp,
  onStartOnChange,
  onChange,
  incrementMinutes = 15,
  fullDayHours = 8,
}) {
  const start = parseHM(startTime, DEFAULT_START);
  const derivedHours = Math.max(0, (parseHM(endTime, DEFAULT_START + fullDayHours * 60) - start) / 60);
  const hours = hoursProp != null ? Math.max(0, hoursProp) : derivedHours;

  // The start row can be controlled by the parent (Schedule's "specific time"
  // flag) or managed internally (Timesheets). When uncontrolled, reveal it
  // up-front for an existing entry whose start isn't the generic default.
  const isStartControlled = startOnProp !== undefined && typeof onStartOnChange === 'function';
  const [internalStartOn, setInternalStartOn] = useState(
    () => !!startTime && parseHM(startTime, DEFAULT_START) !== DEFAULT_START
  );
  const startOn = isStartControlled ? startOnProp : internalStartOn;
  const setStartOn = (v) => {
    if (isStartControlled) onStartOnChange(v);
    else setInternalStartOn(v);
  };

  const step = incrementMinutes / 60;

  const emit = (h, s) => {
    const safeHours = Math.max(0, h);
    onChange({
      hours: safeHours,
      startTime: toHM(s),
      endTime: toHM(s + safeHours * 60),
    });
  };

  const setHours = (h) => emit(Math.max(0, Math.min(MAX_HOURS, h)), start);
  const setStart = (mins) => emit(hours, mins);
  const setEnd = (mins) => emit(Math.max(0, Math.min(MAX_HOURS, (mins - start) / 60)), start);

  // Click-to-edit typed duration. The readout stays a display until clicked,
  // then becomes a text field with validation (0 to MAX_HOURS).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const draftParsed = parseDuration(draft);
  const draftInvalid = draft.trim() !== '' && (draftParsed == null || draftParsed > MAX_HOURS);

  const startEdit = () => {
    setDraft(fmtDur(hours));
    setEditing(true);
  };

  const commitEdit = () => {
    if (draftParsed != null && draftParsed >= 0 && draftParsed <= MAX_HOURS) {
      setHours(draftParsed);
    }
    setEditing(false);
  };

  const handleEditKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!draftInvalid && draft.trim() !== '') commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  const presetValues = [...new Set([0.5, 1, 2, 4, 6, fullDayHours])]
    .filter((v) => v > 0 && v <= MAX_HOURS)
    .sort((a, b) => a - b);

  const startOptions = [];
  for (let m = 0; m < 1440; m += incrementMinutes) {
    startOptions.push({ value: toHM(m), label: fmtClock(m) });
  }
  // Keep the current start visible even if it falls between increments.
  const currentStartValue = toHM(start);
  if (!startOptions.some((o) => o.value === currentStartValue)) {
    startOptions.push({ value: currentStartValue, label: fmtClock(start) });
    startOptions.sort((a, b) => parseHM(a.value, 0) - parseHM(b.value, 0));
  }

  const end = start + hours * 60;
  const endOptions = [];
  for (let m = 0; m < 1440; m += incrementMinutes) {
    endOptions.push({ value: toHM(m), label: fmtClock(m) });
  }
  // Keep the current end visible even if it falls between increments.
  const currentEndValue = toHM(end);
  if (!endOptions.some((o) => o.value === currentEndValue)) {
    endOptions.push({ value: currentEndValue, label: fmtClock(end) });
    endOptions.sort((a, b) => parseHM(a.value, 0) - parseHM(b.value, 0));
  }

  // Track, fill and thumb are drawn as plain elements positioned from this
  // one ratio, rather than painting a CSS gradient behind the native thumb
  // and hoping the browser's internal thumb geometry lines up with it -
  // that geometry isn't a stable pixel formula and drifts in and out of
  // sync with a gradient-based fill at different values. THUMB is our own
  // thumbVisual size, so this math is self-consistent by construction.
  const THUMB = 18;
  const fillRatio = Math.min(1, Math.max(0, hours / MAX_HOURS));
  const thumbLeft = `calc(${fillRatio} * (100% - ${THUMB}px))`;
  const fillWidth = `calc(${fillRatio} * (100% - ${THUMB}px) + ${THUMB / 2}px)`;

  return (
    <div className={styles.scrubber}>
      <div className={styles.headerRow}>
        <span className={styles.heading}>Time</span>
        <span className={styles.totalPill}>{fmtDur(hours)} total</span>
      </div>

      <div className={styles.readout}>
        {editing ? (
          <>
            <input
              autoFocus
              type="text"
              inputMode="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleEditKey}
              onBlur={commitEdit}
              className={`${styles.readoutInput} ${draftInvalid ? styles.readoutInputInvalid : ''}`}
              aria-label="Duration"
              aria-invalid={draftInvalid}
            />
            <div className={`${styles.readoutHint} ${draftInvalid ? styles.readoutHintInvalid : ''}`}>
              {draftInvalid
                ? `Enter a valid duration up to ${MAX_HOURS}h (e.g. 7h 30m, 7:30, 1.5)`
                : 'Press Enter to apply · e.g. 7h 30m, 7:30, 1.5'}
            </div>
          </>
        ) : (
          <div
            className={styles.readoutDisplay}
            onClick={startEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                startEdit();
              }
            }}
            role="button"
            tabIndex={0}
            title="Click to type a duration"
          >
            {fmtDur(hours)}
          </div>
        )}
      </div>

      <div className={styles.sliderWrap}>
        <div className={styles.trackVisual} />
        <div className={styles.fillVisual} style={{ width: fillWidth }} />
        <input
          type="range"
          min={0}
          max={MAX_HOURS}
          step={step}
          value={Math.min(hours, MAX_HOURS)}
          onChange={(e) => setHours(parseFloat(e.target.value))}
          className={styles.range}
        />
        <div className={styles.thumbVisual} style={{ left: thumbLeft }} />
      </div>
      <div className={styles.ticks}>
        <span>0h</span>
        <span>3h</span>
        <span>6h</span>
        <span>9h</span>
        <span>12h</span>
      </div>

      <div className={styles.presets}>
        {presetValues.map((v) => (
          <button
            type="button"
            key={v}
            className={`${styles.chip} ${Math.abs(v - hours) < 1e-6 ? styles.chipActive : ''}`}
            onClick={() => setHours(v)}
          >
            {fmtDur(v)}
          </button>
        ))}
      </div>

      <div className={styles.startSection}>
        {!startOn ? (
          <button type="button" className={styles.addStartBtn} onClick={() => setStartOn(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add a start time <span className={styles.optional}>(optional)</span>
          </button>
        ) : (
          <div>
            <div className={styles.startRow}>
              <div className={styles.startField}>
                <label className={styles.fieldLabel}>Start time</label>
                <CustomDropdown
                  options={startOptions}
                  value={currentStartValue}
                  onChange={(val) => setStart(parseHM(val, start))}
                  fullWidth
                />
              </div>
              <div className={styles.arrow}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </div>
              <div className={styles.startField}>
                <label className={styles.fieldLabel}>Ends at</label>
                <CustomDropdown
                  options={endOptions}
                  value={currentEndValue}
                  onChange={(val) => setEnd(parseHM(val, end))}
                  fullWidth
                />
              </div>
            </div>
            <button type="button" className={styles.removeStartBtn} onClick={() => setStartOn(false)}>
              Remove start time
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
